import React, { useState, useEffect, memo, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Video, Maximize2 } from 'lucide-react';
import LazyImage from '../LazyImage';

const GalleryThumbnail = memo(({ item, index, isActive, onClick, getYouTubeThumbnail }) => {
  const handleClick = useCallback(() => {
    onClick(index);
  }, [index, onClick]);

  return (
    <button
      onClick={handleClick}
      className={`gallery-thumbnail ${isActive ? 'active' : ''}`}
      data-testid={`gallery-thumbnail-${index}`}
    >
      {item.type === 'video' ? (
        <div className="video-thumbnail-wrapper">
          <LazyImage 
            src={getYouTubeThumbnail(item.url)} 
            alt={`${item.title || 'Spectacle pyrotechnique CYGA EVENEMENT'} - Vidéo ${index + 1}`}
            className="video-thumbnail-image"
          />
          <div className="video-play-overlay">
            <Video size={32} />
          </div>
        </div>
      ) : (
        <LazyImage 
          src={item.url} 
          alt={`${item.title || 'Spectacle pyrotechnique CYGA EVENEMENT'} - Photo ${index + 1}`}
        />
      )}
    </button>
  );
});

GalleryThumbnail.displayName = 'GalleryThumbnail';

const GallerySection = memo(({ galleryItems, getYouTubeThumbnail }) => {
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isCustomFullscreen, setIsCustomFullscreen] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const nextGalleryImage = useCallback(() => {
    setGalleryIndex((prev) => (prev + 1) % galleryItems.length);
    setIsVideoLoaded(false);
  }, [galleryItems.length]);

  const prevGalleryImage = useCallback(() => {
    setGalleryIndex((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
    setIsVideoLoaded(false);
  }, [galleryItems.length]);

  const handleThumbnailClick = useCallback((index) => {
    setGalleryIndex(index);
    setIsVideoLoaded(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isCustomFullscreen) {
      setIsCustomFullscreen(false);
      document.body.style.overflow = '';
      return;
    }
    
    const isInNativeFullscreen = !!(document.fullscreenElement || 
                                    document.webkitFullscreenElement || 
                                    document.mozFullScreenElement || 
                                    document.msFullscreenElement);
    
    if (isInNativeFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      return;
    }
    
    setIsCustomFullscreen(true);
    document.body.style.overflow = 'hidden';
  }, [isCustomFullscreen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isCustomFullscreen) {
        setIsCustomFullscreen(false);
        document.body.style.overflow = '';
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isCustomFullscreen]);

  // Preload next image for smoother transitions
  useEffect(() => {
    if (galleryItems.length > 1) {
      const nextIndex = (galleryIndex + 1) % galleryItems.length;
      const nextItem = galleryItems[nextIndex];
      if (nextItem && nextItem.type !== 'video') {
        const img = new Image();
        img.src = nextItem.url;
      }
    }
  }, [galleryIndex, galleryItems]);

  const currentItem = useMemo(() => galleryItems[galleryIndex], [galleryItems, galleryIndex]);

  if (galleryItems.length === 0) return null;

  return (
    <section id="galerie" className={`gallery gallery-section ${isCustomFullscreen ? 'custom-fullscreen' : ''}`} data-testid="gallery-section">
      <div className="section-header">
        <h2 className="section-title" data-testid="gallery-title">DÉCOUVREZ NOTRE TRAVAIL</h2>
        <p className="section-subtitle">Une sélection de nos plus beaux spectacles</p>
      </div>

      <div className="gallery-container">
        <button 
          className="gallery-nav gallery-nav-left gallery-nav-desktop" 
          onClick={prevGalleryImage} 
          data-testid="gallery-prev"
          aria-label="Image précédente"
        >
          <ChevronLeft />
        </button>
        
        <div className="gallery-main-wrapper">
          <div className="gallery-main">
            {currentItem?.type === 'video' ? (
              <>
                {!isVideoLoaded && (
                  <div className="gallery-video-placeholder" onClick={() => setIsVideoLoaded(true)}>
                    <LazyImage 
                      src={getYouTubeThumbnail(currentItem.url)}
                      alt="Vidéo YouTube"
                      className="gallery-video-thumb"
                    />
                    <div className="gallery-play-button">
                      <Video size={64} />
                      <span>Cliquez pour lire</span>
                    </div>
                  </div>
                )}
                {isVideoLoaded && (
                  <iframe
                    src={currentItem.url}
                    title="Vidéo YouTube"
                    className="gallery-video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                    allowFullScreen
                    loading="lazy"
                    data-testid="gallery-video"
                  ></iframe>
                )}
              </>
            ) : (
              <img 
                src={currentItem?.url} 
                alt="Spectacle pyrotechnique" 
                className="gallery-image" 
                data-testid="gallery-image"
                loading="eager"
              />
            )}
          </div>
          
          <div className="gallery-nav-mobile">
            <button className="gallery-nav-btn-mobile" onClick={prevGalleryImage} aria-label="Image précédente">
              <ChevronLeft size={24} />
            </button>
            <button className="gallery-nav-btn-mobile" onClick={nextGalleryImage} aria-label="Image suivante">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <button 
          className="gallery-nav gallery-nav-right gallery-nav-desktop" 
          onClick={nextGalleryImage} 
          data-testid="gallery-next"
          aria-label="Image suivante"
        >
          <ChevronRight />
        </button>
      </div>

      <div className="gallery-thumbnails">
        {galleryItems.map((item, index) => (
          <GalleryThumbnail
            key={index}
            item={item}
            index={index}
            isActive={index === galleryIndex}
            onClick={handleThumbnailClick}
            getYouTubeThumbnail={getYouTubeThumbnail}
          />
        ))}
      </div>

      <button 
        className="gallery-fullscreen-btn-bottom" 
        onClick={toggleFullscreen}
        title="Mode plein écran"
        data-testid="fullscreen-button"
      >
        <Maximize2 size={24} />
        <span>Plein écran</span>
      </button>
    </section>
  );
});

GallerySection.displayName = 'GallerySection';

export default GallerySection;
